import { useMemo, useState } from 'react';
import {
	Button,
	Group,
	Modal,
	MultiSelect,
	SegmentedControl,
	Select,
	Stack,
	Text,
	TextInput,
} from '@mantine/core';
import type { FlowPredicate, FlowRule } from '@dealstack/shared/lib/common/business/documentFlow';
import type { DataPointWithSampleRes } from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/BindPanel.types.ts';

import {
	dataPointChoices,
	toDataPointChoiceSource,
} from '@dealstack/shared/lib/documents/dataPointChoices';
import { isDataPointCollected } from '@dealstack/shared/lib/documents/programmeDataRequirements';

import { DECLARATION_PATH_PREFIX, describePredicate } from './rules.utils';
import type { NewRule, RuleCohort, SegmentOption } from './rules.types';

interface Props {
	opened: boolean;
	dataPoints: DataPointWithSampleRes[];
	/** The programme's data switches that are off; a test on data behind one is not offered. */
	uncollectedRequirements?: readonly string[];
	cohorts: RuleCohort[];
	/**
	 * The rule being changed, or absent to write a new one.
	 *
	 * The caller is expected to key this component on the id, so the controls seed from the rule
	 * on open rather than from an effect that watches it.
	 */
	editing?: { id: string; rule: FlowRule; usedIn: number };
	/** A new rule to create and mark the selection with, or an edited one to store in place. */
	onSubmit: (rule: NewRule) => void;
	onClose: () => void;
}

/** Which kind of test a stored predicate was written with, so editing reopens on that tab. */
const kindOf = (predicate: FlowPredicate): TestKind => {
	if (predicate.operator === 'inCohort') {
		return 'cohort';
	}
	if (predicate.operator === 'isOneOf' || predicate.operator === 'isNoneOf') {
		return 'value';
	}
	return predicate.operator === 'isYes' || predicate.operator === 'isNo' ? 'yesno' : 'blank';
};

const isNegated = (predicate: FlowPredicate): boolean =>
	predicate.operator === 'isNo' ||
	predicate.operator === 'isNoneOf' ||
	predicate.operator === 'isBlank';

/** Which of the safe tests the author is writing. */
type TestKind = 'blank' | 'value' | 'yesno' | 'cohort';

const TEST_KINDS: { value: TestKind; label: string }[] = [
	{ value: 'value', label: 'A specific value' },
	{ value: 'blank', label: 'Is filled in' },
	{ value: 'yesno', label: 'A yes / no answer' },
	{ value: 'cohort', label: 'A cohort' },
];

/** How each data-point test reads its two ways round. */
const POLARITY_OPTIONS: Record<Exclude<TestKind, 'cohort'>, SegmentOption[]> = {
	yesno: [
		{ value: 'positive', label: 'is Yes' },
		{ value: 'negative', label: 'is No' },
	],
	value: [
		{ value: 'positive', label: 'is' },
		{ value: 'negative', label: 'is not' },
	],
	blank: [
		{ value: 'positive', label: 'is filled in' },
		{ value: 'negative', label: 'is blank' },
	],
};

const NO_UNCOLLECTED_REQUIREMENTS: readonly string[] = [];

/**
 * Writes one rule: which test decides whether the selected content appears.
 *
 * Only three tests exist, and the picker is what keeps them safe. Choosing the KIND first means
 * the data points offered are already the ones the test can be asked of — yes/no offers only the
 * declarations, which are the only real booleans — so a comparison that cannot be answered
 * honestly is not reachable rather than merely discouraged. There is no "is more than" and no
 * "and/or": both need type information the data-point catalogue does not carry yet, and an
 * untyped comparison in a deed fails invisibly.
 */
export const RuleModal = ({
	opened,
	dataPoints,
	uncollectedRequirements = NO_UNCOLLECTED_REQUIREMENTS,
	cohorts,
	editing,
	onSubmit,
	onClose,
}: Props) => {
	const editedWhen = editing?.rule.when;
	const editedPath =
		editedWhen === undefined || editedWhen.operator === 'inCohort'
			? undefined
			: editedWhen.dataPointPath;
	const [kind, setKind] = useState<TestKind>(
		editedWhen === undefined ? 'value' : kindOf(editedWhen)
	);
	const [dataPointPath, setDataPointPath] = useState<string | null>(editedPath ?? null);
	const [cohortId, setCohortId] = useState<string | null>(
		editedWhen?.operator === 'inCohort' ? editedWhen.cohortId : null
	);
	const [negated, setNegated] = useState(
		editedWhen === undefined ? false : isNegated(editedWhen)
	);
	const [chosenValues, setChosenValues] = useState<string[]>(
		editedWhen?.operator === 'isOneOf' || editedWhen?.operator === 'isNoneOf'
			? editedWhen.values
			: []
	);
	const [label, setLabel] = useState(editing?.rule.label ?? '');

	// What the programme collects, plus the point the edited rule already tests so it reopens on
	// it. `dataPoints` stays whole for labelling.
	const offerablePoints = useMemo(
		() =>
			dataPoints.filter(
				(point) =>
					point.path === editedPath ||
					isDataPointCollected(point.path, uncollectedRequirements)
			),
		[dataPoints, editedPath, uncollectedRequirements]
	);
	const declarationPoints = useMemo(
		() => offerablePoints.filter((point) => point.path.startsWith(DECLARATION_PATH_PREFIX)),
		[offerablePoints]
	);
	// Only points that declare where their values come from can be compared — anything else is
	// free text, and an equality test against free text is the failure this design exists to
	// avoid, not a feature it is missing.
	const comparablePoints = useMemo(
		() =>
			offerablePoints.filter((point) => toDataPointChoiceSource(point.choices) !== undefined),
		[offerablePoints]
	);
	// Not declarations: an unanswered one is stored as `false`, which is never blank, so "is filled
	// in" would be true for everyone. Not filtered paths either: the test reads the underlying
	// value, not the sum or list the filter prints.
	const fillablePoints = useMemo(
		() =>
			offerablePoints.filter(
				(point) =>
					!point.path.startsWith(DECLARATION_PATH_PREFIX) && !point.path.includes('|')
			),
		[offerablePoints]
	);
	const pointsByKind: Record<TestKind, DataPointWithSampleRes[]> = {
		yesno: declarationPoints,
		value: comparablePoints,
		blank: fillablePoints,
		cohort: dataPoints,
	};
	const offered = pointsByKind[kind];

	const chosenPoint = offered.find((point) => point.path === dataPointPath);
	const valueOptions = useMemo(() => {
		const source = toDataPointChoiceSource(chosenPoint?.choices);
		return source === undefined ? [] : dataPointChoices(source);
	}, [chosenPoint?.choices]);

	const options = useMemo(
		() =>
			offered.map((point) => ({
				value: point.path,
				label: `${point.group} · ${point.label}`,
			})),
		[offered]
	);

	const predicate = useMemo<FlowPredicate | null>(() => {
		if (kind === 'cohort') {
			const cohortName = cohorts.find((cohort) => cohort.id === cohortId)?.name;
			return cohortId === null
				? null
				: {
						operator: 'inCohort',
						cohortId,
						...(cohortName === undefined ? {} : { cohortName }),
					};
		}
		if (dataPointPath === null) {
			return null;
		}
		if (kind === 'yesno') {
			return { operator: negated ? 'isNo' : 'isYes', dataPointPath };
		}
		if (kind === 'value') {
			// Only values the chosen point's list holds: a value carried over from another point
			// would never match, while "Reads as" printed it as if it would.
			const values = chosenValues.filter((value) =>
				valueOptions.some((option) => option.value === value)
			);
			return values.length === 0
				? null
				: { operator: negated ? 'isNoneOf' : 'isOneOf', dataPointPath, values };
		}
		return { operator: negated ? 'isBlank' : 'isNotBlank', dataPointPath };
	}, [kind, cohortId, cohorts, dataPointPath, negated, chosenValues, valueOptions]);

	// The test itself when unnamed, so "is No" and "is not" read that way on the page.
	const chosenLabel = (): string => {
		const trimmed = label.trim();
		if (trimmed !== '') {
			return trimmed;
		}
		return predicate === null ? 'Rule' : describePredicate(predicate, dataPoints, cohorts);
	};

	const build = (): NewRule | null => {
		if (predicate === null) {
			return null;
		}
		return {
			// Editing keeps the id, which is what makes one edit move every place it is used.
			id: editing?.id ?? `rule-${crypto.randomUUID()}`,
			rule: { label: chosenLabel(), when: predicate },
		};
	};

	// No reset on close: the caller keys this component on the rule being edited, so a fresh
	// mount already starts from the right values and a manual reset could only disagree with it.
	const close = onClose;

	return (
		<Modal
			opened={opened}
			onClose={close}
			title={editing === undefined ? 'Show this only if…' : `Edit “${editing.rule.label}”`}
			size="lg"
			data-testid="rule-modal"
		>
			<Stack gap="md">
				<SegmentedControl
					fullWidth
					value={kind}
					onChange={(value) => {
						setKind(
							TEST_KINDS.find((entry) => entry.value === value)?.value ?? 'blank'
						);
						setDataPointPath(null);
						setCohortId(null);
						setNegated(false);
						setChosenValues([]);
					}}
					data={TEST_KINDS}
				/>

				{kind === 'cohort' ? (
					<Select
						label="Which cohort"
						description="Membership is maintained by an admin in Classifiers — an investor who joins later is outside it until somebody adds them."
						placeholder="Pick a cohort"
						searchable
						value={cohortId}
						onChange={setCohortId}
						data={cohorts.map((cohort) => ({ value: cohort.id, label: cohort.name }))}
						data-testid="rule-cohort"
					/>
				) : (
					<>
						<Select
							label="Which data point"
							placeholder={
								kind === 'yesno'
									? 'Pick a declaration'
									: 'Pick anything the document can read'
							}
							searchable
							value={dataPointPath}
							onChange={(value) => {
								setDataPointPath(value);
								setChosenValues([]);
							}}
							data={options}
							nothingFoundMessage="No data point matches that"
							data-testid="rule-data-point"
						/>
						<SegmentedControl
							fullWidth
							value={negated ? 'negative' : 'positive'}
							onChange={(value) => setNegated(value === 'negative')}
							data={POLARITY_OPTIONS[kind]}
						/>
						{kind === 'value' && (
							<MultiSelect
								label="Which value"
								description="Picked from the same list the value was recorded from, so it always matches what is stored. Choose more than one for “either”."
								placeholder={chosenValues.length === 0 ? 'Pick one or more' : ''}
								searchable
								disabled={dataPointPath === null}
								value={chosenValues}
								onChange={setChosenValues}
								data={valueOptions}
								nothingFoundMessage="No value matches that"
								data-testid="rule-values"
							/>
						)}
					</>
				)}

				<TextInput
					label="Call this rule"
					description="Shown on the document beside the content it controls."
					placeholder={chosenLabel()}
					value={label}
					onChange={(event) => setLabel(event.currentTarget.value)}
					data-testid="rule-label"
				/>

				{editing !== undefined && editing.usedIn > 1 && (
					<Text size="sm" c="orange.8" data-testid="rule-edit-impact">
						This rule is used in <b>{editing.usedIn} places</b>. Changing it changes all
						of them, including any not on screen.
					</Text>
				)}

				{predicate !== null && (
					<Text size="sm" c="dark.0">
						Reads as: <b>{describePredicate(predicate, dataPoints, cohorts)}</b>. The
						selected content appears only for those recipients; everyone else does not
						see it
						{predicate.operator === 'isBlank'
							? '.'
							: ', including anyone with no answer recorded or one the list does not recognise.'}
					</Text>
				)}

				<Group justify="flex-end">
					<Button variant="default" onClick={close}>
						Cancel
					</Button>
					<Button
						disabled={predicate === null}
						onClick={() => {
							const built = build();
							if (built !== null) {
								onSubmit(built);
								close();
							}
						}}
						data-testid="rule-apply"
					>
						{editing === undefined ? 'Apply' : 'Save changes'}
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
};
